import React from "react"
import { useTranslation } from "react-i18next"

import { homeColor, homeSize, homeText } from "../home-theme.ts"
import {
  HOME_SECTION_SPECS,
  type HomeSectionId,
  sectionAnchorId
} from "./homeSectionModel.ts"

/**
 * The sticky section nav overlaps whatever a jump lands on, so a heading
 * scrolled to by `scrollIntoView` has to reserve the nav's height above
 * itself. CSS does this natively and keeps working for a plain `#hash` load,
 * which a JS-computed scroll offset would not.
 */
const SCROLL_MARGIN = 64

interface HomeSectionBandProps {
  id: HomeSectionId
  /**
   * Replaces the section's standing caption. Used where the line has to say
   * something only this render knows — the window a figure was measured over,
   * which §6 requires be distinguishable from an as-of-today balance.
   */
  caption?: React.ReactNode
  /** Sits at the heading's right edge — "View all actions", "Open Locations". */
  action?: React.ReactNode
}

/**
 * One section's heading, OUTSIDE the cards it introduces.
 *
 * §2 asks for headings outside the cards specifically to stop the page
 * nesting a bordered header inside a bordered card inside a bordered section.
 * The band draws no border and no background of its own for the same reason.
 *
 * It is also the section nav's scroll target, so it carries the anchor id and
 * `tabIndex={-1}`: a heading that cannot take focus leaves a keyboard user's
 * next Tab back at the nav, having apparently done nothing.
 */
export const HomeSectionBand = ({
  id,
  caption,
  action,
  children
}: React.PropsWithChildren<HomeSectionBandProps>) => {
  const { t } = useTranslation()
  const spec = HOME_SECTION_SPECS.find((candidate) => candidate.id === id)
  if (!spec) return null

  return (
    <section
      aria-labelledby={`${sectionAnchorId(id)}-title`}
      className={"min-w-0"}
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div
        id={sectionAnchorId(id)}
        tabIndex={-1}
        style={{
          scrollMarginTop: SCROLL_MARGIN,
          display: "flex",
          alignItems: "baseline",
          gap: 12,
          flexWrap: "wrap",
          outline: "none"
        }}
      >
        <h2
          id={`${sectionAnchorId(id)}-title`}
          className={"m-0"}
          style={{ ...homeText.sectionTitle, color: homeColor.ink }}
        >
          {t(spec.titleKey, spec.title)}
        </h2>
        <span style={{ ...homeText.meta, color: homeColor.inkMuted }}>
          {caption ?? t(spec.captionKey, spec.caption)}
        </span>
        {action ? (
          <div style={{ marginInlineStart: "auto" }}>{action}</div>
        ) : null}
      </div>
      <div
        className={"min-w-0"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: homeSize.sectionGap
        }}
      >
        {children}
      </div>
    </section>
  )
}
